import { Badge } from "@/components/ui/Badge";
import { BANK_LOGOS } from "@/lib/constants";
import type { BankCardEditFormProps } from "@/types/settings";
import { useBankSettings } from "@shared/contexts";
import type { BankProviderEntry } from "@shared/types/settings";
import { getBankLabel } from "@shared/types/settings";
import { CheckCircle, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "../../pages/SettingsPage.styles";

const BankCard = ({ bank }: { bank: BankProviderEntry }) => {
  const { t } = useTranslation("settings");
  const settings = useBankSettings();

  const [saving, setSaving] = useState(false);
  const isEditing = settings.editingId === bank.id;

  if (isEditing) {
    return renderEditForm({
      bank,
      saving,
      setSaving,
      editForm: settings.editForm,
      patchEditForm: settings.patchEditForm,
      handleUpdateBank: settings.handleUpdateBank,
      stopEditing: settings.stopEditing,
      t,
    });
  }

  return (
    <div className={styles.bankCard}>
      <div className={styles.cardHeaderRow}>
        <span className={styles.bankName}>{bank.name}</span>
        <Badge variant="info" size="sm">
          {getBankLabel(bank.type)}
        </Badge>
        <span className={styles.logoWrapper}>
          <img src={BANK_LOGOS[bank.type]} alt={getBankLabel(bank.type)} className={styles.logoImg} />
        </span>
      </div>

      {bank.isDefault && (
        <div className={styles.defaultRow}>
          <CheckCircle size={14} className={styles.bankDefaultIcon} />
          <Badge variant="success" size="sm">
            {t("defaultBank")}
          </Badge>
        </div>
      )}

      {!bank.isDefault && settings.banks.length > 1 && (
        <button onClick={() => settings.handleSetDefault(bank.id)} className={styles.setDefaultButton}>
          {t("setDefault")}
        </button>
      )}

      {(bank.isDefault || settings.banks.length > 1) &&
        renderActionButtons({ bank, startEdit: settings.startEdit, handleDelete: settings.handleDelete, t })}
    </div>
  );
};

const renderActionButtons = ({
  bank,
  startEdit,
  handleDelete,
  t,
}: {
  bank: BankProviderEntry;
  startEdit: (b: BankProviderEntry) => void;
  handleDelete: (id: number) => void;
  t: ReturnType<typeof useTranslation>["t"];
}) => (
  <div className={styles.actionButtonRow}>
    <button onClick={() => startEdit(bank)} className={styles.editButton}>
      <Pencil size={14} /> {t("edit")}
    </button>
    <button onClick={() => handleDelete(bank.id)} className={styles.deleteButton}>
      <Trash2 size={16} /> {t("delete")}
    </button>
  </div>
);

const EditForm = ({
  bank,
  saving,
  setSaving,
  editForm,
  patchEditForm,
  handleUpdateBank,
  stopEditing,
  t,
}: BankCardEditFormProps & { t: ReturnType<typeof useTranslation>["t"] }) => (
  <div className={styles.bankCard}>
    <input
      type="text"
      value={editForm.name}
      onChange={(e) => patchEditForm({ name: e.target.value })}
      placeholder={t("bankNameInputPlaceholder")}
      autoComplete="off"
      className={styles.input}
    />

    <span className={styles.logoWrapper}>
      <img src={BANK_LOGOS[bank.type]} alt={getBankLabel(bank.type)} className={`${styles.logoImg} ${styles.bankSelectLogo}`} />
    </span>

    <input
      type="text"
      value={editForm.username}
      onChange={(e) => patchEditForm({ username: e.target.value })}
      placeholder={`${t("currentUsername")}${bank.username !== null ? ` (${t("leaveEmptyToKeep")})` : ""}`}
      autoComplete="off"
      className={styles.input}
    />

    <input
      type="password"
      value={editForm.password}
      onChange={(e) => patchEditForm({ password: e.target.value })}
      placeholder={`${t("newPassword")}${bank.password !== null ? ` (${t("leaveEmptyToKeep")})` : ""}`}
      autoComplete="off"
      className={styles.input}
    />

    <div className={styles.formActions}>
      <button
        onClick={() => {
          setSaving(true);
          handleUpdateBank(bank.id, editForm.name, editForm.username || undefined, editForm.password || undefined).finally(() => setSaving(false));
        }}
        disabled={saving || !editForm.name.trim()}
        className={styles.saveButton}
      >
        {saving ? t("saving") : t("update")}
      </button>
      <button onClick={() => stopEditing()} className={styles.cancelButton}>
        {t("cancel")}
      </button>
    </div>
  </div>
);

const renderEditForm = ({
  bank,
  saving,
  setSaving,
  editForm,
  patchEditForm,
  handleUpdateBank,
  stopEditing,
  t,
}: BankCardEditFormProps & { t: ReturnType<typeof useTranslation>["t"] }) => (
  <EditForm
    bank={bank}
    saving={saving}
    setSaving={setSaving}
    editForm={editForm}
    patchEditForm={patchEditForm}
    handleUpdateBank={handleUpdateBank}
    stopEditing={stopEditing}
    t={t}
  />
);

export { BankCard };
